import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Heart, Send, MessageCircle, Loader2, Lock } from 'lucide-react';
import { useMatching } from '@/hooks/useMatching';
import { MatchRequestCard } from '@/components/MatchRequestCard';
import { MatchChatCard } from '@/components/MatchChatCard';
import { ChatWindow } from '@/components/ChatWindow';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '@/hooks/useSubscription';
import { useTrialStatus } from '@/hooks/useTrialStatus';
import { Alert, AlertDescription } from '@/components/ui/alert';

const MatchInbox = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const {
    receivedRequests,
    sentRequests,
    activeMatches,
    loadingReceived,
    loadingSent,
    loadingMatches
  } = useMatching();
  const { subscription } = useSubscription();
  const { isInTrial } = useTrialStatus();

  const [selectedChatRoomId, setSelectedChatRoomId] = useState<string | null>(null);
  const [selectedMatchInfo, setSelectedMatchInfo] = useState<any>(null);

  const pendingReceived = receivedRequests?.filter(r => r.status === 'pending') || [];
  
  // FREE users limited to 1 active chat
  const maxChats = subscription?.data?.subscription_tier === 'free' && !isInTrial ? 1 : Infinity;
  const activeChatsCount = activeMatches?.length || 0;

  // Auto-open chat if coming from notification
  useEffect(() => {
    const state = location.state as { openMatchId?: string };
    if (state?.openMatchId && activeMatches) {
      const matchToOpen = activeMatches.find(m => m.id === state.openMatchId);
      if (matchToOpen) {
        handleOpenChat(matchToOpen);
        // Clear state to prevent re-opening on component re-render
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state, activeMatches]);

  const handleOpenChat = (match: any) => {
    if (match.chat_room?.id) {
      setSelectedChatRoomId(match.chat_room.id);
      setSelectedMatchInfo(match);
    }
  };

  const handleCloseChat = () => {
    setSelectedChatRoomId(null);
    setSelectedMatchInfo(null);
  };

  return (
    <div className="container mx-auto max-w-4xl p-4 pb-20">
      <div className="mb-6">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Heart className="h-8 w-8 text-pink-500" />
          {t('matchInbox.title')}
        </h1>
        <p className="text-muted-foreground mt-2">
          {t('matchInbox.subtitle')}
        </p>
      </div>

      <Tabs defaultValue="received" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="received" className="flex items-center gap-2">
            <Heart className="h-4 w-4" />
            {t('matchInbox.tabs.received')}
            {pendingReceived.length > 0 && (
              <Badge variant="destructive" className="ml-1">
                {pendingReceived.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="sent" className="flex items-center gap-2">
            <Send className="h-4 w-4" />
            {t('matchInbox.tabs.sent')}
          </TabsTrigger>
          <TabsTrigger value="matches" className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4" />
            {t('matchInbox.tabs.chats')}
            {activeMatches && activeMatches.length > 0 && (
              <Badge className="ml-1">{activeMatches.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Received Requests */}
        <TabsContent value="received" className="mt-6">
          {loadingReceived ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : receivedRequests && receivedRequests.length > 0 ? (
            <div className="space-y-4">
              {receivedRequests.map(request => (
                <MatchRequestCard
                  key={request.id}
                  request={request}
                  type="received"
                  onOpenChat={handleOpenChat}
                />
              ))}
            </div>
          ) : (
            <Card className="p-12 text-center">
              <Heart className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-2">{t('matchInbox.emptyStates.noReceived')}</h3>
              <p className="text-muted-foreground">
                {t('matchInbox.emptyStates.noReceivedDesc')}
              </p>
            </Card>
          )}
        </TabsContent>

        {/* Sent Requests */}
        <TabsContent value="sent" className="mt-6">
          {loadingSent ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : sentRequests && sentRequests.length > 0 ? (
            <div className="space-y-4">
              {sentRequests.map(request => (
                <MatchRequestCard
                  key={request.id}
                  request={request}
                  type="sent"
                />
              ))}
            </div>
          ) : (
            <Card className="p-12 text-center">
              <Send className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-2">{t('matchInbox.emptyStates.noSent')}</h3>
              <p className="text-muted-foreground">
                {t('matchInbox.emptyStates.noSentDesc')}
              </p>
            </Card>
          )}
        </TabsContent>

        {/* Active Matches / Chats */}
        <TabsContent value="matches" className="mt-6">
          {activeChatsCount >= maxChats && subscription?.data?.subscription_tier === 'free' && !isInTrial && (
            <Alert className="mb-4 border-amber-200 bg-amber-50">
              <Lock className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-900">
                <span className="font-medium">FREE Limit Reached:</span> You have {maxChats} active chat. Upgrade for unlimited chats!
              </AlertDescription>
            </Alert>
          )}
          {loadingMatches ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : activeMatches && activeMatches.length > 0 ? (
            <div className="space-y-4">
              {activeMatches.map(match => (
                <MatchChatCard
                  key={match.id}
                  match={match}
                  onOpenChat={() => handleOpenChat(match)}
                />
              ))}
            </div>
          ) : (
            <Card className="p-12 text-center">
              <MessageCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-2">{t('matchInbox.emptyStates.noChats')}</h3>
              <p className="text-muted-foreground">
                {t('matchInbox.emptyStates.noChatsDesc')}
              </p>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Chat Window */}
      {selectedChatRoomId && selectedMatchInfo && (
        <ChatWindow
          chatRoomId={selectedChatRoomId}
          matchInfo={selectedMatchInfo}
          onClose={handleCloseChat}
        />
      )}
    </div>
  );
};

export default MatchInbox;
